/**
 * Pedalling IK — the pure maths behind the rider shadow's moving legs.
 *
 * The shadow caster (render/riderCaster.ts) is a clone of
 * `assets/models/cyclist_rig.glb`, the same art as cyclist.glb but with the
 * rider's legs cut into four FK nodes:
 *
 *   body                       bike + torso + arms + head (legless)
 *   thigh_L  -> shin_L         left  hip joint -> knee joint (shin = calf+foot+pedal+crank)
 *   thigh_R  -> shin_R         right hip joint -> knee joint
 *
 * Each thigh node's ORIGIN is its hip joint and each shin node's origin is its
 * knee joint, so a rotation about the node's local X axis pivots exactly at the
 * anatomical joint. Everything below therefore reduces to a planar two-link IK
 * problem in the (y, z) plane — x never moves, because the only rotation axis
 * is X.
 *
 * ANGLE CONVENTION. A 2-D direction in the (y, z) plane is described by
 * `phi = atan2(y, z)`: 0 is straight ahead (+Z, the direction the model faces),
 * +90° is straight up. A three.js `rotation.x = r` maps (0,1,0) -> (0,cos r,
 * sin r), i.e. it DECREASES phi by r. Hence `r = phiRest - phi` throughout, and
 * a solved phi equal to its rest value yields exactly zero node rotation — the
 * authored pose is reproduced bit-for-bit at crank phase 0.
 *
 * ELBOW SIGN. A two-link IK has two solutions (knee forward / knee backward).
 * Rather than guess, the sign is DERIVED from the authored rest pose: whichever
 * branch reproduces the authored thigh angle is the branch used for every
 * phase. The rest pose is anatomically correct (knee forward/up, toward +Z), so
 * the whole revolution is too, and the configuration can never flip mid-stroke.
 *
 * CRANK CIRCLE. The two rest feet sit exactly opposite each other on a circle
 * about the crank axis; each leg's own rest radius and rest angle are measured
 * from the constants below, so phase 0 is the authored pose *exactly* and the
 * two legs are automatically half a revolution apart. Those measured radii come
 * out at ~0.095 m, shorter than the authored 0.128 m crank arm — the foot
 * reference points sit inside the pedal circle. Deriving the radius from the
 * rest feet is what makes phase 0 exact; an A/B at 0.128 m (which would break
 * that) was indistinguishable in the cast shadow, so exactness wins.
 */

/** A 2-D point in the model's (y, z) plane (x is invariant under rotation.x). */
export interface YZ {
  readonly y: number;
  readonly z: number;
}

// ---------------------------------------------------------------------------
// Measured model constants (assets/models/cyclist_rig.glb, glTF units = metres)
// ---------------------------------------------------------------------------

/** Uniform scale carried by the `thigh_*` nodes in the GLB. */
export const RIG_NODE_SCALE = 0.95;

/** Node names in cyclist_rig.glb. */
export const RIG_NODES = {
  thighL: "thigh_L",
  shinL: "shin_L",
  thighR: "thigh_R",
  shinR: "shin_R",
} as const;

/** Hip joints = `thigh_*` node translations (model space). */
export const HIP_L: YZ = { y: 0.241, z: -0.413 };
export const HIP_R: YZ = { y: 0.24, z: -0.412 };

/** Knee offsets = `shin_*` node translations, in the thigh's LOCAL frame. */
export const SHIN_LOCAL_L: YZ = { y: -0.28315791487693787, z: 0.46421054005622864 };
export const SHIN_LOCAL_R: YZ = { y: -0.21368420124053955, z: 0.5484211444854736 };

/** Crank axis centre (model space); the pedals orbit about the X axis. */
export const CRANK_CENTER: YZ = { y: -0.643, z: -0.102 };

/** Authored rest foot positions (model space) — antipodal on the crank circle. */
export const FOOT_REST_L: YZ = { y: -0.709, z: -0.034 };
export const FOOT_REST_R: YZ = { y: -0.577, z: -0.171 };

/**
 * Metres of ground travel per crank revolution — a 700c wheel on a ~2.7 gear
 * ratio. At the 15.6 m/s start speed this is ~2.6 crank Hz, a believable
 * (if brisk) cadence, and it keeps the legs visibly faster as the run speeds up.
 */
export const METRES_PER_CRANK_REV = 6.0;

/**
 * Crank rotation sense. Forward pedalling carries the top of the stroke toward
 * the front of the bike (+Z), i.e. from phi = 90° toward phi = 0°, so phi
 * DECREASES as the phase advances.
 */
export const CRANK_DIR = -1;

/** Numerical margin (m) kept away from the fully folded / fully straight limits. */
const REACH_EPS = 1e-3;

// ---------------------------------------------------------------------------
// Solver
// ---------------------------------------------------------------------------

/** Everything precomputed once per leg from the authored rest pose. */
export interface LegSolver {
  /** Hip joint, model space. */
  readonly hip: YZ;
  /** Crank circle centre, model space. */
  readonly center: YZ;
  /** Crank radius traced by THIS leg's foot reference point (m). */
  readonly radius: number;
  /** Crank angle (phi) of this leg's foot at rest — its phase offset. */
  readonly phaseRest: number;
  /** Hip -> knee length (m). */
  readonly l1: number;
  /** Knee -> foot length (m). */
  readonly l2: number;
  /** Authored thigh direction angle (phi of hip -> knee). */
  readonly phi1Rest: number;
  /** Authored shin direction angle (phi of knee -> foot), in ABSOLUTE terms. */
  readonly phi2Rest: number;
  /** Which IK branch reproduces the authored pose: +1 or -1. */
  readonly elbowSign: 1 | -1;
}

/** Local X rotations for one leg's two nodes, as deltas from the rest pose. */
export interface LegPose {
  /** `thigh_*.rotation.x`. */
  readonly thighX: number;
  /** `shin_*.rotation.x` (LOCAL — the shin inherits the thigh's rotation). */
  readonly shinX: number;
}

const angleOf = (dy: number, dz: number): number => Math.atan2(dy, dz);
const wrapPi = (a: number): number =>
  Math.atan2(Math.sin(a), Math.cos(a));

/**
 * Build a leg solver from the authored geometry. Everything — link lengths,
 * rest angles, crank radius and phase offset, and the IK branch — is derived
 * from the rest pose, so `solveLeg(solver, 0)` returns exactly `{0, 0}`.
 *
 * @param hip       hip joint (= thigh node translation), model space.
 * @param shinLocal knee offset in the thigh's local frame (= shin node translation).
 * @param footRest  authored foot reference point, model space.
 * @param center    crank axis centre, model space.
 * @param nodeScale uniform scale on the thigh node (scales `shinLocal`).
 */
export function makeLegSolver(
  hip: YZ,
  shinLocal: YZ,
  footRest: YZ,
  center: YZ = CRANK_CENTER,
  nodeScale: number = RIG_NODE_SCALE,
): LegSolver {
  const knee: YZ = {
    y: hip.y + nodeScale * shinLocal.y,
    z: hip.z + nodeScale * shinLocal.z,
  };
  const l1 = Math.hypot(knee.y - hip.y, knee.z - hip.z);
  const l2 = Math.hypot(footRest.y - knee.y, footRest.z - knee.z);
  const phi1Rest = angleOf(knee.y - hip.y, knee.z - hip.z);
  const phi2Rest = angleOf(footRest.y - knee.y, footRest.z - knee.z);

  const radius = Math.hypot(footRest.y - center.y, footRest.z - center.z);
  const phaseRest = angleOf(footRest.y - center.y, footRest.z - center.z);

  // Which IK branch is the authored one? Solve the rest triangle both ways and
  // keep the sign that reproduces phi1Rest.
  const d = Math.hypot(footRest.y - hip.y, footRest.z - hip.z);
  const psi = angleOf(footRest.y - hip.y, footRest.z - hip.z);
  const alpha = Math.acos(
    Math.min(1, Math.max(-1, (l1 * l1 + d * d - l2 * l2) / (2 * l1 * d))),
  );
  const elbowSign: 1 | -1 =
    Math.abs(wrapPi(phi1Rest - (psi + alpha))) <=
    Math.abs(wrapPi(phi1Rest - (psi - alpha)))
      ? 1
      : -1;

  return { hip, center, radius, phaseRest, l1, l2, phi1Rest, phi2Rest, elbowSign };
}

/** This leg's foot target on the crank circle at the given crank phase. */
export function footTarget(solver: LegSolver, phase: number): YZ {
  const phi = solver.phaseRest + CRANK_DIR * phase;
  return {
    y: solver.center.y + solver.radius * Math.sin(phi),
    z: solver.center.z + solver.radius * Math.cos(phi),
  };
}

/**
 * Solve one leg for a crank phase (radians; 0 = the authored rest pose).
 *
 * Returns node rotations as DELTAS from the authored pose, so writing them to
 * `thigh.rotation.x` / `shin.rotation.x` is always safe and phase 0 restores
 * the GLB exactly.
 */
export function solveLeg(solver: LegSolver, phase: number): LegPose {
  const target = footTarget(solver, phase);
  const { hip, l1, l2, elbowSign } = solver;

  const dy = target.y - hip.y;
  const dz = target.z - hip.z;
  const psi = angleOf(dy, dz);
  // Clamp the reach so acos can never go out of domain even if the crank
  // constants are later retuned into an unreachable configuration.
  const dRaw = Math.hypot(dy, dz);
  const d = Math.min(
    l1 + l2 - REACH_EPS,
    Math.max(Math.abs(l1 - l2) + REACH_EPS, dRaw),
  );

  const alpha = Math.acos(
    Math.min(1, Math.max(-1, (l1 * l1 + d * d - l2 * l2) / (2 * l1 * d))),
  );
  const phi1 = psi + elbowSign * alpha;

  // Knee position implied by the solved thigh angle, then the shin's absolute
  // direction from that knee to the target.
  const kneeY = hip.y + l1 * Math.sin(phi1);
  const kneeZ = hip.z + l1 * Math.cos(phi1);
  const phi2 = angleOf(target.y - kneeY, target.z - kneeZ);

  // rotation.x = phiRest - phi (see ANGLE CONVENTION above); the shin is a
  // child of the thigh, so its LOCAL rotation is the absolute delta minus the
  // thigh's.
  const thighX = wrapPi(solver.phi1Rest - phi1);
  const shinX = wrapPi(solver.phi2Rest - phi2) - thighX;
  return { thighX, shinX };
}

/** The two solvers for the authored rig, built once. */
export const LEG_SOLVER_L: LegSolver = makeLegSolver(HIP_L, SHIN_LOCAL_L, FOOT_REST_L);
export const LEG_SOLVER_R: LegSolver = makeLegSolver(HIP_R, SHIN_LOCAL_R, FOOT_REST_R);

/**
 * Advance the crank phase by one frame of ground travel.
 *
 * @param phase        current crank phase (rad).
 * @param forwardSpeed rider speed (m/s).
 * @param dt           frame time (s).
 */
export function advanceCrankPhase(
  phase: number,
  forwardSpeed: number,
  dt: number,
): number {
  const next =
    phase + ((forwardSpeed * dt) / METRES_PER_CRANK_REV) * Math.PI * 2;
  // Keep the accumulator bounded so float precision never degrades in a long run.
  const TAU = Math.PI * 2;
  return ((next % TAU) + TAU) % TAU;
}
